import { inject, Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { AuthResponse, LoginRequest, User, WalletAccount } from '../models/auth.models';
import { AccountService } from './account.service';
import { CommonService } from './common.service';
import { UserStateService } from './user-state.service';
import { WebStorageService } from './web-storage.service';
import { WalletStateService } from './wallet-state.service';
import { SocketService } from './socket.service';
import { CsrfTokenService } from './csrf-token.service';
import { ExitQuestionService } from './exit-question.service';

interface LogoutOptions {
  manual?: boolean;
  showToast?: boolean;
  toastMessage?: string;
  redirectTo?: string | null;
  broadcast?: boolean;
  broadcastReason?: string;
  broadcastUserId?: string | null;
  skipServerLogout?: boolean;
}

export interface LogoutBroadcastEvent {
  reason: string;
  userId: string | null;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements CanActivate, CanActivateChild {
  readonly logoutBroadcastStorageKey = 'rw:auth:logout';
  private readonly legacyLoginUrl = environment.API_BASE_URL_V3 + '/regulated-sign-in';
  private readonly sessionUrl = environment.API_BASE_URL_V3 + '/auth/me';
  private readonly logoutUrl = environment.API_BASE_URL.replace('/api/v1', '/api/v3') + '/market-place/auth/logout';
  private sessionBootstrapPromise: Promise<void> | null = null;
  private hasResolvedSession = false;
  private logoutInProgress = false;
  private readonly exitQuestionService = inject(ExitQuestionService);

  constructor(
    private readonly http: HttpClient,
    private readonly accountService: AccountService,
    private readonly userState: UserStateService,
    private readonly webStorageService: WebStorageService,
    private readonly walletState: WalletStateService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly socketService: SocketService,
    private readonly csrfTokenService: CsrfTokenService,
    private readonly ngZone: NgZone,
    private readonly commonService: CommonService
  ) { }

  initializeSession(): Promise<void> {
    if (!this.sessionBootstrapPromise) {
      // Auth state is held in-memory only; legacy browser storage is wiped on
      // bootstrap for security. The server-side session cookie is the single
      // source of truth, so always probe /user/auth/me — if there is no valid
      // cookie the endpoint returns 401 and catchError below handles it.
      this.sessionBootstrapPromise = firstValueFrom(
        this.fetchCurrentUserFromSession().pipe(
          catchError(() => of(null)),
          map(() => void 0)
        )
      );
    }

    return this.sessionBootstrapPromise;
  }

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(this.legacyLoginUrl, credentials).pipe(
      tap((response) => {
        if (response?.data) {
          this.syncAuthenticatedUserState(response.data, { regulated: true });
        }
      })
    );
  }

  logout(options: LogoutOptions = {}): Observable<void> {
    const {
      manual = true,
      showToast = false,
      toastMessage = 'Logged out successfully.',
      redirectTo = null,
      broadcast = false,
      broadcastReason = 'LOGOUT',
      broadcastUserId,
      skipServerLogout = false
    } = options;

    if (this.logoutInProgress) {
      return of(void 0);
    }

    if (manual) {
      this.accountService.markManualLogout();
    }

    if (broadcast) {
      this.broadcastLogoutEvent({
        reason: broadcastReason,
        userId: this.normalizeUserId(broadcastUserId ?? this.userState.user?._id),
        timestamp: Date.now()
      });
    }

    this.logoutInProgress = true;
    this.exitQuestionService.endSession();

    const logoutRequest$ = skipServerLogout
      ? of(void 0)
      : this.http.post<void>(this.logoutUrl, {}).pipe(
        catchError(() => of(void 0))
      );

    return logoutRequest$.pipe(
      tap(() => {
        if (showToast) {
          this.toastr.success(toastMessage);
        }
      }),
      finalize(() => {
        this.clearAuthState();
        this.logoutInProgress = false;

        if (redirectTo) {
          void this.ngZone.run(() => this.router.navigateByUrl(redirectTo, { replaceUrl: true }));
        }
      })
    );
  }

  getCurrentUser(forceRefresh: boolean = false): Observable<User | null> {
    const inMemoryUser = this.userState.user;
    if (inMemoryUser && !forceRefresh) {
      return of(inMemoryUser);
    }

    if (!forceRefresh && this.hasResolvedSession) {
      return of(null);
    }

    return this.fetchCurrentUserFromSession();
  }

  isAuthenticated(forceRefresh: boolean = false): Observable<boolean> {
    return this.getCurrentUser(forceRefresh).pipe(
      map((user) => !!user)
    );
  }

  clearAuthState(): void {
    this.sessionBootstrapPromise = null;
    this.csrfTokenService.clear();
    // Drop cached reference data so a different user logging in within the same
    // SPA session (no page reload) never reuses the previous session's data.
    // (KYC status is cleared centrally in UserStateService.clearAuthState below,
    // which every logout path — including 401 session-expiry — also reaches.)
    this.commonService.clearReferenceDataCache();
    this.socketService.disconnect();
    this.walletState.clearStoredRegulatedMode();
    this.userState.clearAuthState(true);
    this.webStorageService.removeLocalStorageItem('user');
    this.webStorageService.removeLocalStorageItem('account');
    this.webStorageService.removeLocalStorageItem('regulated');
    this.webStorageService.removeLocalStorageItem('has_new_item');
    this.webStorageService.removeLocalStorageItem('token');
    this.webStorageService.removeItem('token');
    this.webStorageService.removeLocalStorageItem('user_register_type');
    this.webStorageService.removeLocalStorageItem('lend_path_visited');
  }

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const url = state.url;
    const isPreAuthOnlyRoute = this.isPreAuthOnlyRoute(url);

    // Fire email-verification BEFORE /auth/me when the URL carries a
    // verification signature, so newly-registered users (no session yet)
    // get their email marked verified server-side before the session probe
    // runs. Without this, /auth/me returns 401 → user stays null → the
    // verification modal that triggers confirmEmail never opens.
    const userSource$ = this.runEmailVerificationIfNeeded(url);

    return userSource$.pipe(
      map((user) => {
        if (!user) {
          return true;
        }

        if (this.requiresEmailVerification(user)) {
          return this.getUnverifiedAccessResult(user, url, isPreAuthOnlyRoute);
        }

        if (this.matchesTopLevelPath(url, ['confirm-email'])) {
          return true;
        }

        return isPreAuthOnlyRoute
          ? this.getRedirectForVerifiedUser(url)
          : true;
      }),
      catchError(() => of(true))
    );
  }

  // Dedup per-token so canActivateChild bounces (or any re-run of the
  // guard) don't re-fire the verification PATCH.
  private readonly verifiedTokens = new Set<string>();

  private runEmailVerificationIfNeeded(url: string): Observable<User | null> {
    const verificationParams = this.extractVerificationParams(url);
    if (!verificationParams || this.verifiedTokens.has(verificationParams.token)) {
      return this.getCurrentUser(false);
    }

    this.verifiedTokens.add(verificationParams.token);
    return this.accountService.confirmEmail(verificationParams).pipe(
      catchError(() => of(null)),
      switchMap(() => this.getCurrentUser(true))
    );
  }

  private extractVerificationParams(url: string): { id: string; token: string } | null {
    const queryIndex = url.indexOf('?');
    if (queryIndex < 0) {
      return null;
    }
    const params = new URLSearchParams(url.substring(queryIndex + 1));
    const id = params.get('id');
    const token = params.get('token');
    return id && token ? { id, token } : null;
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }

  private requiresEmailVerification(user: User | null | undefined): boolean {
    return user?.email_verified === false;
  }

  private getUnverifiedAccessResult(
    user: User,
    url: string,
    isPreAuthOnlyRoute: boolean
  ): boolean | UrlTree {
    if (!this.resolveRegulatedFlag(user)) {
      return this.getSelfCustodyUnverifiedAccessResult(url, isPreAuthOnlyRoute);
    }

    return this.isAllowedBeforeVerification(url)
      ? true
      : this.getVerificationRedirect(user);
  }

  private getSelfCustodyUnverifiedAccessResult(url: string, isPreAuthOnlyRoute: boolean): boolean | UrlTree {
    if (this.matchesTopLevelPath(url, ['confirm-email'])) {
      return true;
    }

    return isPreAuthOnlyRoute
      ? this.router.parseUrl('/dashboard')
      : true;
  }

  private isPreAuthOnlyRoute(url: string): boolean {
    return this.matchesTopLevelPath(url, [
      'verify-email',
      'confirm-email',
      'sign-up',
      'sign-in',
      'borrow-register',
      'lend-register',
      'forgot-password',
      'reset-password'
    ]);
  }

  private isAllowedBeforeVerification(url: string): boolean {
    return this.matchesTopLevelPath(url, [
      'verify-email',
      'confirm-email',
      'sign-up',
      'sign-in',
      'forgot-password',
      'reset-password'
    ]);
  }

  private getVerificationRedirect(user: User): UrlTree {
    return user?.email
      ? this.router.createUrlTree(['/verify-email'], { queryParams: { email: user.email } })
      : this.router.parseUrl('/verify-email');
  }

  private getRedirectForVerifiedUser(url: string): UrlTree {
    if (this.matchesTopLevelPath(url, ['borrow-register'])) {
      return this.router.parseUrl('/borrow');
    }

    if (this.matchesTopLevelPath(url, ['lend-register'])) {
      return this.router.parseUrl('/lend');
    }

    return this.router.parseUrl('/dashboard');
  }

  /**
   * Match the top-level route segment of an Angular router URL against a
   * known set of paths. Uses Router.parseUrl so we compare structured segments
   * rather than performing brittle substring matches.
   */
  private matchesTopLevelPath(url: string, paths: readonly string[]): boolean {
    const topSegment = this.getTopLevelSegment(url);
    if (!topSegment) {
      return false;
    }
    return paths.includes(topSegment);
  }

  private getTopLevelSegment(url: string): string | null {
    try {
      const urlTree = this.router.parseUrl(url);
      const segments = urlTree.root.children['primary']?.segments ?? [];
      return segments.length > 0 ? segments[0].path : null;
    } catch {
      return null;
    }
  }

  private fetchCurrentUserFromSession(): Observable<User | null> {
    return this.http.get<AuthResponse>(this.sessionUrl).pipe(
      map((response) => response?.data ?? null),
      tap((user) => {
        this.hasResolvedSession = true;
        if (user) {
          this.applyAuthenticatedUser(user);
        } else {
          this.clearAuthState();
        }
      }),
      catchError((error) => {
        if (error?.status === 401 || error?.status === 403) {
          this.hasResolvedSession = true;
          this.clearAuthState();
          return of(null);
        }

        return throwError(() => error);
      })
    );
  }

  syncAuthenticatedUserState(
    user: User,
    options: {
      regulated?: boolean;
      fallbackAccount?: WalletAccount | null;
    } = {}
  ): void {
    this.hasResolvedSession = true;
    const isRegulated = typeof options.regulated === 'boolean'
      ? options.regulated
      : this.resolveRegulatedFlag(user);
    const accountData = this.buildAccountState(user, isRegulated, options.fallbackAccount ?? this.walletState.getWallet());

    this.userState.setUser(user);
    this.userState.setRegulated(isRegulated);
    this.userState.setAccount(accountData);
    this.walletState.setRegulated(isRegulated);
    this.walletState.clearExplicitWalletReconnectRequirement();
    this.webStorageService.setLocalStorage('user', user);
    this.webStorageService.setLocalStorage('account', accountData);
    this.webStorageService.setLocalStorage('regulated', isRegulated);
  }

  private applyAuthenticatedUser(user: User): void {
    this.syncAuthenticatedUserState(user);
  }

  private resolveRegulatedFlag(user: User): boolean {
    const rawAuthType = user['auth_type'] ?? user['login_type'] ?? user['custodial_type'];
    if (typeof rawAuthType === 'string') {
      const normalizedAuthType = rawAuthType.trim().toLowerCase();
      if (normalizedAuthType.includes('self') || normalizedAuthType.includes('non')) {
        return false;
      }

      if (normalizedAuthType.includes('regulated') || normalizedAuthType.includes('custodial')) {
        return true;
      }
    }

    const currentRegulatedState = this.walletState.getRegulated();
    const explicitServerRegulatedValue = this.parseRegulatedValue(user['is_regulated']);

    // Only preserve the "regulated" state when the user is genuinely custodial
    // (has a Fireblocks address). Without this guard, self-custody users who
    // log in via email get stuck as regulated because the wallet hasn't
    // reconnected yet at session-restore time.
    if (explicitServerRegulatedValue === false && currentRegulatedState === true && !this.hasSelfCustodySignal() && !!user['fire_block_address']) {
      return true;
    }

    if (explicitServerRegulatedValue !== null) {
      return explicitServerRegulatedValue;
    }

    // Final fallback: the presence of a Fireblocks address is the definitive
    // marker for a custodial (regulated) user. Falling back to stale wallet
    // state would default to `true` on a fresh session and mis-classify
    // self-custody users — blocking the allowance popup that's only meant
    // for them.
    return !!user['fire_block_address'];
  }

  private buildAccountState(
    user: User,
    isRegulated: boolean,
    fallbackAccount: WalletAccount | null
  ): WalletAccount {
    const fallbackNetwork = fallbackAccount?.networkId ?? Number(environment.DEFAULT_NETWORK);
    const fallbackChainId = fallbackAccount?.chainId || environment.DEFAULT_CHAIN;
    const fallbackProvider = fallbackAccount?.walletProvider ?? '';

    return {
      walletAddress: user.wallet_address || fallbackAccount?.walletAddress || '',
      chainId: isRegulated ? environment.DEFAULT_CHAIN : fallbackChainId,
      walletProvider: isRegulated ? '' : fallbackProvider,
      networkId: isRegulated ? Number(environment.DEFAULT_NETWORK) : fallbackNetwork,
      init: false
    };
  }

  private parseRegulatedValue(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return true;
      }

      if (normalized === 'false' || normalized === '0') {
        return false;
      }
    }

    return null;
  }

  private hasSelfCustodySignal(): boolean {
    const wallet = this.walletState.getWallet();
    const provider = wallet?.walletProvider ?? this.walletState.getRecentConnectorId();
    if (this.isSelfCustodyProvider(provider)) {
      return true;
    }

    if (this.walletState.getWagmiConnected()) {
      return true;
    }

    const wagmiAccount = this.walletState.getWagmiStore()?.state?.data?.account
      ?? this.walletState.getWagmiStore()?.state?.data?.address;
    return typeof wagmiAccount === 'string' && wagmiAccount.length > 0;
  }

  private isSelfCustodyProvider(provider: string | null | undefined): boolean {
    return provider === 'metamask'
      || provider === 'walletconnect'
      || provider === 'io.metamask'
      || provider === 'walletConnect';
  }

  parseLogoutBroadcast(rawValue: string | null): LogoutBroadcastEvent | null {
    if (!rawValue) {
      return null;
    }

    try {
      const parsedEvent = JSON.parse(rawValue) as Partial<LogoutBroadcastEvent>;
      return {
        reason: typeof parsedEvent.reason === 'string' ? parsedEvent.reason : 'LOGOUT',
        userId: this.normalizeUserId(parsedEvent.userId),
        timestamp: typeof parsedEvent.timestamp === 'number' ? parsedEvent.timestamp : Date.now()
      };
    } catch {
      return null;
    }
  }

  private broadcastLogoutEvent(event: LogoutBroadcastEvent): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(this.logoutBroadcastStorageKey, JSON.stringify(event));
    } catch {
      // Ignore storage failures; logout should still proceed in the current tab.
    }
  }

  private normalizeUserId(userId: unknown): string | null {
    if (userId === null || userId === undefined) {
      return null;
    }

    const normalizedUserId = String(userId).trim();
    return normalizedUserId.length ? normalizedUserId : null;
  }
}
